import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "src/@/components/ui/dialog";
import { Input } from "src/@/components/ui/input";
import { Button } from "src/@/components/ui/button";
import Iconify from "src/components/Iconify";
import { v2_actionListarProdutos } from "src/actions/v2/cadastro/v2_actionProdutos";
import Scrollbar from 'src/components/Scrollbar';

export default function ModalSearchProduct({ open, onClose, onSelect, ownerId }) {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && ownerId) {
            fetchProducts();
        }
    }, [open, ownerId]);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await v2_actionListarProdutos(ownerId);
            // Ensure we have an array even if the response is unexpected
            const data = response.data?.data || response.data || [];
            setProducts(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching products:", error);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredProducts = products.filter(product => {
        const productName = product?.name || "";
        return productName.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const handleSelect = (product) => {
        onSelect(product);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl w-full h-[600px] flex flex-col p-0 gap-0 overflow-hidden bg-background border-border shadow-2xl rounded-2xl">
                <div className="p-4 border-b border-border bg-card">
                    <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <DialogTitle className="text-lg font-bold text-foreground flex items-center gap-2">
                            <Iconify icon="solar:box-bold" className="w-5 h-5 text-primary" />
                            Buscar Produto
                        </DialogTitle>
                    </DialogHeader>

                    <div className="mt-4 relative">
                        <Iconify
                            icon="solar:magnifer-linear"
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                        />
                        <Input
                            placeholder="Buscar por nome..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 bg-muted/50 border-input focus-visible:ring-primary h-10"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-hidden relative bg-background">
                    {loading ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                            <Iconify icon="eos-icons:loading" className="w-8 h-8 text-primary animate-spin" />
                            <span className="text-sm text-muted-foreground font-medium">Carregando produtos...</span>
                        </div>
                    ) : (
                        <Scrollbar className="h-full">
                            <div className="p-4 space-y-2">
                                {filteredProducts.length > 0 ? (
                                    filteredProducts.map((product) => (
                                        <div
                                            key={product._id || product.id}
                                            onClick={() => handleSelect(product)}
                                            className="group flex items-center gap-3 p-3 rounded-xl hover:bg-primary/5 cursor-pointer transition-all border border-transparent hover:border-primary/10"
                                        >
                                            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0 border border-border">
                                                {product.image ? (
                                                    <img
                                                        src={product.image}
                                                        alt={product.name}
                                                        className="w-full h-full object-cover transition-transform group-hover:scale-110"
                                                    />
                                                ) : (
                                                    <Iconify icon="solar:box-bold" className="w-6 h-6 text-muted-foreground" />
                                                )}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                                                    {product.name}
                                                </h4>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">
                                                        {product.price ? `R$ ${product.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'R$ 0,00'}
                                                    </span>
                                                    {product.stock !== undefined && (
                                                        <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                                            Estoque: {product.stock}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary hover:bg-primary/10"
                                            >
                                                <Iconify icon="solar:add-circle-bold" className="w-5 h-5" />
                                            </Button>
                                        </div>
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-16 text-center">
                                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                            <Iconify icon="solar:box-minimalistic-linear" className="w-8 h-8 text-muted-foreground" />
                                        </div>
                                        <h3 className="text-sm font-semibold text-foreground">Nenhum produto encontrado</h3>
                                        <p className="text-xs text-muted-foreground mt-1 max-w-[200px]">
                                            Tente ajustar sua busca ou cadastre um novo produto.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </Scrollbar>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}